/**
 * Widget HTML Generators
 *
 * Standalone HTML generators for pending/stopped widgets.
 * These are separated from GlobalChatSystem/styles.ts to avoid circular dependencies.
 *
 * IMPORTANT: The CSS animations referenced here (pendingShimmer, pendingSpin, pendingGlow)
 * must be defined in GlobalChatSystem via PendingWidgetGlobalStyles.
 */

/**
 * Generate inline HTML for the pending widget (for use in streaming message placeholders)
 * @param statusText - The text to display (e.g., "researching", "formatting")
 *
 * The words are --brand-gold-text (2026-09-24): gold is a fill, never text on a
 * light ground. --brand-gold-hover measured 2.58:1 on the pill's own shimmer;
 * gold-text is 5.2:1 at its darkest stop (#f5f0e4).
 */
export const getPendingWidgetHtml = (statusText: string): string => `
<div class="pending-widget-wrapper" style="position:relative;display:inline-flex;align-items:center;gap:12px;padding:10px 18px;background:linear-gradient(135deg,var(--brand-canvas) 0%,#f5f0e4 50%,var(--brand-canvas) 100%);background-size:200% 100%;animation:pendingShimmer 3s ease-in-out infinite,pendingGlow 2s ease-in-out infinite;border-radius:24px;border:1px solid rgba(198,167,94,0.15);font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;font-size:13px;font-weight:500;color:var(--brand-gold-text);letter-spacing:0.01em;user-select:none;" aria-live="polite" aria-busy="true" role="status">
  <div class="pending-spinner" style="width:16px;height:16px;border:2px solid rgba(198,167,94,0.2);border-top-color:var(--brand-gold);border-radius:50%;animation:pendingSpin 0.8s linear infinite;flex-shrink:0;"></div>
  <span style="color:var(--brand-gold-text);font-weight:500;">${statusText}</span>
  <button id="pending-widget-stop-btn" class="pending-stop-btn" aria-label="Stop" title="stop" style="width:8px;height:8px;min-width:8px;min-height:8px;max-width:8px;max-height:8px;border:none;border-radius:1px;background:var(--brand-black);cursor:pointer;display:none;margin-left:6px;flex-shrink:0;transition:background 0.15s ease;padding:0;box-sizing:border-box;appearance:none;-webkit-appearance:none;line-height:0;"></button>
</div>
`;

/**
 * The pending widget, anchored BELOW streaming content (2026-08-13).
 *
 * During a long document run the preview grows to dozens of pages. A reader
 * who scrolls to the bottom — which is where reading naturally ends — used to
 * find the text just stopping mid-sentence: the only progress affordance was
 * the banner at the TOP, long out of view. On a 110-page run that read as
 * "it cut off and died", while nine of ten sections were still translating.
 *
 * The growing edge of the stream must therefore always END in the platform's
 * own animated pending widget, so wherever the user is, "still working" is
 * visible at the exact point where the content currently ends.
 */
export const getStreamingTailHtml = (statusText: string): string => `
<div class="streaming-tail-wrapper" style="margin-top:14px;">${
  // The real pending widget owns the #pending-widget-stop-btn id and its
  // click wiring; a second copy of that id inside the stream body could
  // hijack it. The tail is purely visual, so the button is stripped.
  getPendingWidgetHtml(statusText).replace(/<button id="pending-widget-stop-btn"[\s\S]*?<\/button>/, '')
}</div>
`;

/**
 * Progress widget for long multi-section jobs (2026-08-12).
 *
 * A 200-page translation runs for several minutes across ~34 concurrent
 * sections. The pending widget alone gives no sense of whether it is moving,
 * so this adds the count and a determinate bar. Sits above the streaming
 * preview rather than replacing it.
 *
 * LAYOUT (2026-08-20): the count and the percentage are pushed apart with
 * space-between, which only separates them when the row has slack. The
 * wrapper had no width of its own, so in a narrow chat panel the box shrank
 * to fit its text, space-between had nothing to distribute, and the two
 * labels rendered flush: "section 0 of 25" + "0%" read as "section 0 of
 * 250%". Hence the explicit full width AND a gap, so they stay legible at any
 * width even if the box shrinks again.
 *
 * INK (2026-09-24): the words are gold-text (5.6:1 on the canvas) and the
 * percentage the muted ink (5.2:1). Gold-hover measured 2.79:1 and #8A7742
 * 4.19:1 here — gold is a fill on a light ground, never the words.
 *
 * @param statusText - what is happening (e.g. "translating to english")
 * @param completed  - sections finished so far
 * @param total      - sections in the document
 */
export const getSectionProgressHtml = (
  statusText: string,
  completed: number,
  total: number,
): string => {
  const safeTotal = Math.max(1, Math.floor(total) || 1);
  const safeDone = Math.min(safeTotal, Math.max(0, Math.floor(completed) || 0));
  const pct = Math.round((safeDone / safeTotal) * 100);
  return `
<div class="section-progress-wrapper" style="display:flex;flex-direction:column;gap:6px;width:100%;box-sizing:border-box;padding:10px 14px;margin-bottom:12px;background:var(--brand-canvas);border:1px solid rgba(198,167,94,0.22);border-radius:12px;font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;" aria-live="polite" role="status">
  <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;font-size:12.5px;font-weight:500;color:var(--brand-gold-text);letter-spacing:0.01em;">
    <span style="flex:1 1 auto;min-width:0;">${statusText} &middot; section ${safeDone} of ${safeTotal}</span>
    <span style="flex:0 0 auto;white-space:nowrap;color:var(--brand-text-muted);font-variant-numeric:tabular-nums;">${pct}%</span>
  </div>
  <div style="width:100%;height:4px;background:rgba(198,167,94,0.18);border-radius:999px;overflow:hidden;">
    <div style="width:${pct}%;height:100%;background:var(--brand-gold);border-radius:999px;transition:width 0.4s ease;"></div>
  </div>
</div>
`;
};

/**
 * Coverage receipt shown with a finished whole-document translation.
 *
 * The pipeline measures how much of the source actually came back (see
 * TranslationCoverage). Before this, that number lived only in a console log,
 * so a client had no way to know a document was complete short of counting
 * paragraphs by hand - which is exactly how the 2026-08-11 shortfall went
 * unnoticed. State it plainly, in both directions.
 */
export const getTranslationCoverageHtml = (
  coverage: {
    totalSections: number;
    shortSections: number;
    failedSections: number;
    ratio: number;
    sourceChars: number;
    complete: boolean;
  },
  options?: { fromOcr?: boolean },
): string => {
  const chars = coverage.sourceChars.toLocaleString('en-US');
  // The receipt is a fidelity claim. When the source text was read off page
  // images rather than an embedded text layer, "complete" means every
  // character we recognised — which is not the same promise, and saying so is
  // the difference between a receipt and a guess.
  const ocrNote = options?.fromOcr
    ? ' Source was a scanned PDF read by transcription, so check names, figures and dates against the original.'
    : '';
  if (coverage.complete) {
    return `
<div class="translation-coverage-receipt" style="display:flex;align-items:center;gap:9px;padding:9px 14px;margin-bottom:12px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;font-size:12.5px;color:#166534;">
  <span aria-hidden="true" style="font-size:13px;">&#10003;</span>
  <span>Complete translation &mdash; all ${coverage.totalSections} sections, ${chars} characters of source text.${ocrNote}</span>
</div>
`;
  }
  const pct = Math.round(coverage.ratio * 100);
  const shortfall = coverage.shortSections + coverage.failedSections;
  return `
<div class="translation-coverage-receipt" style="display:flex;align-items:flex-start;gap:9px;padding:9px 14px;margin-bottom:12px;background:#fffbeb;border:1px solid #fcd34d;border-radius:10px;font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;font-size:12.5px;color:#92400e;">
  <span aria-hidden="true" style="font-size:13px;line-height:1.3;">&#9888;</span>
  <span>Partial translation &mdash; about ${pct}% of the source text. ${shortfall} of ${coverage.totalSections} section${shortfall === 1 ? '' : 's'} came back short. Ask me to retry and I will re-run the missing parts.${ocrNote}</span>
</div>
`;
};

/**
 * Generate inline HTML for the stopped widget (after user cancels an operation)
 * No spinner, no stop button - just a static "stopped" message that persists in chat history.
 *
 * IMPORTANT: This must render as a standalone widget (same styling as pending widget)
 * NOT inside a message bubble with timestamp/copy buttons.
 */
export const getStoppedWidgetHtml = (): string => `
<div class="stopped-widget-wrapper" style="display:inline-flex;align-items:center;gap:8px;padding:10px 18px;background:#f1f5f9;border-radius:24px;border:1px solid var(--brand-line);font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;font-size:13px;font-weight:500;color:var(--brand-text-secondary);letter-spacing:0.01em;user-select:none;">
  <span style="color:#64748b;font-weight:500;">stopped</span>
</div>
`;

/**
 * Generate inline HTML for the interrupted widget (after tab dormancy or timeout)
 * Shows a warning-styled message with reason, encouraging user to retry.
 * @param reason - The reason for interruption (e.g., "Browser tab was inactive")
 */
export const getInterruptedWidgetHtml = (reason: string): string => `
<div class="interrupted-widget-wrapper" style="display:inline-flex;align-items:center;gap:10px;padding:12px 18px;background:#fffbeb;border-radius:12px;border:1px solid #fcd34d;font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;font-size:13px;font-weight:500;color:#92400e;letter-spacing:0.01em;user-select:none;">
  <span style="font-size:16px;">&#9888;</span>
  <span>Operation interrupted${reason ? `: ${reason}` : ''}. Please try again.</span>
</div>
`;
